import type {
  BaseChargeLightDisplayMode,
  BaseCurrentLightEffect,
  BaseGradientLightEffect,
  BaseLightEffect,
} from '../../types/base'
import type { RGBGroup } from '../../types/color'
import type { BaseLightingTarget, LightingEffectDefinition } from '../../types/lighting'
import { FeatureBase } from '../FeatureBase'

declare module '../FeatureMap' {
  export interface DeviceFeatureMap {
    /** 底座灯效能力。只有 manifest.driver 为底座驱动时才会挂载。 */
    baseLighting: IBaseLightingFeature
  }
}

/** 设置普通灯效时传入的参数。 */
export interface BaseLightEffectInput {
  /** 灯效 ID。 */
  mode: number
  /** 亮度，固件范围 0-100。 */
  brightness: number
  /** 速度/频率，固件范围 0-100。 */
  frequency: number
  /** RGB 颜色，按 [R, G, B] 顺序，只取前三个值。 */
  color: [number, number, number] | number[]
  /** 渐变方向，底座当前只使用 3 表示左右渐变。 */
  gradientDirection?: number
  /** 渐变颜色组，传入后由 SDK 在内部转换为渐变端点命令。 */
  rgbGroup?: RGBGroup
  /** 灯效目标，默认底座灯带。 */
  target?: BaseLightingTarget
}

/** 设置渐变灯效单个端点时传入的参数。 */
export interface BaseGradientLightEffectInput {
  /** 渐变灯效 ID，1.0 中通常为 6。 */
  mode: number
  /** 灯珠索引，1.0 常用 0 和 15 表示左右端点。 */
  ledIndex: number
  /** RGB 颜色，按 [R, G, B] 顺序，只取前三个值。 */
  color: [number, number, number] | number[]
  /** 当前端点亮度。 */
  brightness: number
}

export interface IBaseLightingFeature {
  /** 读取 manifest 中配置的底座灯效元数据。 */
  getEffects(target?: BaseLightingTarget): Promise<LightingEffectDefinition[]>
  /** 读取指定灯效的亮度、速度和颜色。 */
  getLightEffect(mode: number, target?: BaseLightingTarget): Promise<BaseLightEffect>
  /** 写入指定灯效的亮度、速度和颜色。 */
  setLightEffect(input: BaseLightEffectInput): Promise<void>
  /** 读取当前正在生效的灯效 ID、充电状态和总开关。 */
  getCurrentEffect(target?: BaseLightingTarget): Promise<BaseCurrentLightEffect>
  /** 切换当前正在生效的灯效 ID。 */
  setCurrentEffect(mode: number, target?: BaseLightingTarget): Promise<void>
  /** 开关底座灯效总开关。 */
  switchLight(isOn: boolean): Promise<void>
  /** 设置鼠标充电时显示氛围灯还是充电灯，当前仅 VT_nrf54L_Base 支持。 */
  setChargeLightDisplayMode(displayMode: BaseChargeLightDisplayMode): Promise<void>
  /** 读取渐变灯效某个灯珠端点的颜色和亮度。 */
  getGradientLightEffect(mode: number, ledIndex: number): Promise<BaseGradientLightEffect>
  /** 写入渐变灯效某个灯珠端点的颜色和亮度。 */
  setGradientLightEffect(input: BaseGradientLightEffectInput): Promise<void>
}

/** 底座灯效 feature 抽象类，具体实现位于 chips/shared/feature-impls/base-lighting。 */
export class BaseLightingFeature extends FeatureBase implements IBaseLightingFeature {
  getEffects(_target?: BaseLightingTarget): Promise<LightingEffectDefinition[]> {
    return this.unsupported('BaseLightingFeature.getEffects')
  }
  getLightEffect(_mode: number, _target?: BaseLightingTarget): Promise<BaseLightEffect> {
    return this.unsupported('BaseLightingFeature.getLightEffect')
  }
  setLightEffect(_input: BaseLightEffectInput): Promise<void> {
    return this.unsupported('BaseLightingFeature.setLightEffect')
  }
  getCurrentEffect(_target?: BaseLightingTarget): Promise<BaseCurrentLightEffect> {
    return this.unsupported('BaseLightingFeature.getCurrentEffect')
  }
  setCurrentEffect(_mode: number, _target?: BaseLightingTarget): Promise<void> {
    return this.unsupported('BaseLightingFeature.setCurrentEffect')
  }
  switchLight(_isOn: boolean): Promise<void> {
    return this.unsupported('BaseLightingFeature.switchLight')
  }
  setChargeLightDisplayMode(_displayMode: BaseChargeLightDisplayMode): Promise<void> {
    return this.unsupported('BaseLightingFeature.setChargeLightDisplayMode')
  }
  getGradientLightEffect(_mode: number, _ledIndex: number): Promise<BaseGradientLightEffect> {
    return this.unsupported('BaseLightingFeature.getGradientLightEffect')
  }
  setGradientLightEffect(_input: BaseGradientLightEffectInput): Promise<void> {
    return this.unsupported('BaseLightingFeature.setGradientLightEffect')
  }
}
